import { createServerFn } from '@tanstack/react-start';

/**
 * Country for the current request, used to pick consent defaults.
 *
 * Resolved server-side so the policy is known before any gtag config call.
 * The GeoLite2 lookup lives behind a dynamic import so the dataset cannot
 * be pulled into the browser bundle — this file is imported from client
 * route code, but the handler body is replaced by an RPC stub there.
 *
 * Never throws. The region is an optional input to the consent defaults and
 * `null` already means "unknown region", which the consent code reads as the
 * strictest defaults. This runs inside the root route's `beforeLoad`, so a
 * rejection here would take down every page of the site — the GeoLite2 lookup
 * failing (missing/corrupt dataset, bundling change, memory pressure) must
 * degrade to strict consent, not a 500.
 */
export const getConsentRegion = createServerFn().handler(
  async (): Promise<string | null> => {
    try {
      const { getClientIp } = await import('@/utils/get-client-ip');
      const { getCountryFromIp } = await import('./get-country-from-ip.server');
      return getCountryFromIp(getClientIp()) ?? null;
    } catch (error) {
      const { captureServerException } =
        await import('@/utils/posthog-server-errors');
      captureServerException(error, undefined, {
        path: '/consent/getConsentRegion',
        errorContext: 'consent region lookup failed; defaulting to unknown',
      });
      return null;
    }
  },
);
